import React from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import {SOURCES_DIALOG_STACK_PROPS} from './sourcesDialogStackProps';
import ClassificationFieldsGroup from '../../../../../components/classification/ClassificationFieldsGroup';
import useClassificationFields from '../../../../../components/classification/useClassificationFields';
import useSourcesUploadQueueStore from './useSourcesUploadQueueStore';
import {isLikelyUrl, normalizeUrlInput, parseUrlLines} from '../../../../../utils/sourceImportHelpers';

/**
 * Dedicated "Add source" dialog for the URL entry point: one or more HTTP(S) URLs (one
 * per line) → POST /api/data-items/from-url (per URL), handed to `onSubmitWebImports`
 * (goes through the web-import queue).
 *
 * Classification Level / Distribution / Compartment are optional; one shared set is
 * applied to every URL entry submitted.
 */
const SourcesUrlImportDialog = ({open, onClose, onSubmitWebImports}) => {
    const defaultClassificationLevel = useSourcesUploadQueueStore((state) => state.classificationLevel);
    const [urlText, setUrlText] = React.useState('');
    const {reset: resetClassification, toPayload: classificationPayload, fieldsProps: classificationFieldsProps} =
        useClassificationFields(defaultClassificationLevel);
    const [error, setError] = React.useState(null);
    const [invalidUrls, setInvalidUrls] = React.useState([]);

    const resetState = React.useCallback(() => {
        setUrlText('');
        resetClassification();
        setError(null);
        setInvalidUrls([]);
    }, [resetClassification]);

    const handleClose = React.useCallback(() => {
        resetState();
        onClose?.();
    }, [onClose, resetState]);

    const handleSubmit = React.useCallback(() => {
        setError(null);
        setInvalidUrls([]);
        const urls = parseUrlLines(urlText);
        if (!urls.length) {
            setError('Enter at least one URL.');
            return;
        }
        const invalid = urls.filter((url) => !isLikelyUrl(url));
        if (invalid.length) {
            setInvalidUrls(invalid);
            setError(`Not a valid http(s) URL: ${invalid[0]}`);
            return;
        }
        const entries = urls.map((url) => ({
            sourceKind: 'url',
            url: normalizeUrlInput(url),
            ...classificationPayload(),
        }));
        onSubmitWebImports?.(entries);
        handleClose();
    }, [classificationPayload, handleClose, onSubmitWebImports, urlText]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            {...SOURCES_DIALOG_STACK_PROPS}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <LinkRoundedIcon fontSize="small" color="action"/>
                    <span>Add source: URL</span>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        label="URLs"
                        placeholder={'https://example.com/article\nhttps://reuters.com/world/...'}
                        value={urlText}
                        onChange={(event) => {
                            setUrlText(event.target.value);
                            if (error) setError(null);
                            if (invalidUrls.length) setInvalidUrls([]);
                        }}
                        fullWidth
                        multiline
                        minRows={4}
                        autoFocus
                        error={Boolean(error)}
                        helperText="Paste one or more web page URLs (one per line). Each page is fetched, converted to a document, and added to your files."
                    />
                    {invalidUrls.length > 0 && (
                        <Stack spacing={0.5} data-testid="invalid-url-lines">
                            {parseUrlLines(urlText).map((url, index) => {
                                const isInvalid = invalidUrls.includes(url);
                                return (
                                    <Box
                                        key={`${url}-${index}`}
                                        data-invalid={isInvalid ? 'true' : 'false'}
                                        sx={{
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            bgcolor: isInvalid ? 'error.light' : 'transparent',
                                            color: isInvalid ? 'error.contrastText' : 'text.secondary',
                                            fontFamily: 'monospace',
                                            fontSize: '0.8rem',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {url}
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                    <ClassificationFieldsGroup
                        idPrefix="url-classification"
                        {...classificationFieldsProps}
                    />
                </Stack>

                {error && (
                    <Alert severity="error" sx={{mt: 2}}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!urlText.trim()}>
                    Add source
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(SourcesUrlImportDialog);
